import styles from "./EnergySavingCTA.module.scss";
import classnames from "classnames/bind";
import { formatAmplienceImageURL } from "@JohnLewisPartnership/jl-design-system/dist/utils/imageUrlModifier/imageUrlModifier";
import { YourekoMedal } from "@/components/EnergySaving/useYourekoLabels.ts";
import { ChevronRightOutlined as ChevronRight } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/ChevronRightOutlined";

const cx = classnames.bind(styles);

type RatingMapType = Record<
  YourekoMedal,
  { medalUrl: string; ratingText: string }
>;

const GBP = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const imageBaseUrl = "https://static.johnlewiscontent.com/v1/static";
const FALLBACK_MEDAL_URL = `${imageBaseUrl}/none-button-v2`;

const ratingMap: RatingMapType = {
  Best: {
    medalUrl: `${imageBaseUrl}/platinum-button-v2%20(1)`,
    ratingText: "",
  },
  Gold: {
    medalUrl: `${imageBaseUrl}/gold-button-v2`,
    ratingText: "Gold rating (top 20%)",
  },
  Silver: {
    medalUrl: `${imageBaseUrl}/silver-button-v2`,
    ratingText: "Silver rating (20-40%)",
  },
  Bronze: {
    medalUrl: `${imageBaseUrl}/bronze-button-v2`,
    ratingText: "Bronze rating (40-60%)",
  },
  "Good value": {
    medalUrl: `${imageBaseUrl}/plain-button-v2`,
    ratingText: "Good value (lower 40%)",
  },
};

type Props = {
  onClick: () => void;
  medalRatingText: YourekoMedal;
  numberOfAlternatives: number;
  energyCostSavingUnformatted: number;
};

export function EnergySavingCTA({
  onClick,
  medalRatingText,
  numberOfAlternatives,
  energyCostSavingUnformatted,
}: Props) {
  const medalUrl = formatAmplienceImageURL(
    ratingMap[medalRatingText]?.medalUrl || FALLBACK_MEDAL_URL,
  );
  const ratingText = ratingMap[medalRatingText]?.ratingText || "";

  function getTitle(energyCostSavingUnformatted: number) {
    if (!Number.isFinite(energyCostSavingUnformatted))
      return "No energy saving";

    if (energyCostSavingUnformatted <= 0) return "No energy saving";

    return `${GBP.format(energyCostSavingUnformatted)} energy saving`;
  }

  return (
    <button onClick={onClick} className={cx("energySavingCTA")}>
      <div className={cx("medalWrapper")}>
        <img
          className={cx("medal")}
          src={medalUrl}
          alt={`${medalRatingText || ""} rating`}
        />
      </div>
      <span className={cx("savingDescription")}>
        {getTitle(energyCostSavingUnformatted)}
        {(numberOfAlternatives > 0 || ratingText) && (
          <span
            className={cx("ratingDescription")}
            data-testid="energy-saving-rating"
          >
            <span className={cx("seperator")}>|</span>
            {numberOfAlternatives > 0
              ? `Found ${numberOfAlternatives} cheaper to run`
              : ratingText}
          </span>
        )}
      </span>
      <ChevronRight size="medium" />
    </button>
  );
}

export default EnergySavingCTA;
